import { z } from "zod";
import axios from "axios";
import { api } from "./client";
import { fetchPublishers, fetchEditions } from "./issues";
import { fetchAuthProfiles } from "./crawlerOps";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/models/coverage.py — keep in sync.

   Expected-vs-produced for one business date. The Today page hides any
   publisher with zero issues; this is the inverse view: what is missing
   and why. Backed by GET /api/dashboard/coverage; when that endpoint is
   not deployed yet (404) we compose an approximation client-side from the
   existing publisher/edition/session endpoints so the page still works.
   ────────────────────────────────────────────────────────────────────── */

export const GAP_CLASSES = [
  "partial", "scheduled_off", "not_activated", "auth_blocked", "pending", "new_gap", "dead",
] as const;
export type GapClass = (typeof GAP_CLASSES)[number];

export const PUB_STATUSES = [
  "ok", "partial", "missing", "pending", "dead", "auth_blocked", "scheduled_off", "none",
] as const;
export type PubStatus = (typeof PUB_STATUSES)[number];

export const MissingEdition = z.object({
  edition_id:     z.number().int(),
  city:           z.string().nullable(),
  edition_name:   z.string().nullable(),
  classification: z.string(),
  last_ok_date:   z.string().nullable(),
  days_since_ok:  z.number().int().nullable(),
  review_reason:  z.string().nullable(),
  has_city_code:  z.boolean(),
  today_status:   z.string().nullable(),
});
export type MissingEdition = z.infer<typeof MissingEdition>;

export const PublisherCoverage = z.object({
  channel_id:    z.number().int(),
  channel_name:  z.string(),
  category:      z.string().nullable(),
  language:      z.string().nullable(),
  expected:      z.number().int(),
  produced:      z.number().int(),
  missing:       z.number().int(),
  pages:         z.number().int(),
  status:        z.string(),
  last_ok_date:  z.string().nullable(),
  days_since_ok: z.number().int().nullable(),
  no_publish_days:        z.string().nullable(),
  scheduled_off_today:    z.boolean(),
  needs_login:            z.boolean(),
  auth_required_for_content: z.boolean(),
  auth_status:            z.string().nullable(),
  auth_failure_count:     z.number().int().nullable(),
  auth_last_validated_at: z.string().nullable(),
  auth_blocked:           z.boolean(),
  missing_editions:       z.array(MissingEdition),
  classification_counts:  z.record(z.number()),
});
export type PublisherCoverage = z.infer<typeof PublisherCoverage>;

export const AttemptError = z.object({
  channel_id:    z.number().int().nullable(),
  channel_name:  z.string().nullable(),
  error_class:   z.string().nullable(),
  error_message: z.string().nullable(),
  http_status:   z.number().int().nullable(),
  attempts:      z.number().int(),
  editions:      z.number().int(),
  last_at:       z.string().nullable(),
});
export type AttemptError = z.infer<typeof AttemptError>;

export const OpenAlert = z.object({
  severity:   z.string().nullable(),
  alert_type: z.string().nullable(),
  source:     z.string().nullable(),
  channel_id: z.number().int().nullable(),
  edition_id: z.number().int().nullable(),
  title:      z.string().nullable(),
  created_at: z.string().nullable(),
});
export type OpenAlert = z.infer<typeof OpenAlert>;

export const CoverageReport = z.object({
  business_date:     z.string(),
  weekday:           z.string(),
  is_today:          z.boolean(),
  crawl_window_open: z.boolean(),
  generated_at:      z.string(),
  totals:            z.record(z.unknown()),
  publishers:        z.array(PublisherCoverage),
  attempt_errors:    z.array(AttemptError),
  open_alerts:       z.array(OpenAlert),
  notes:             z.array(z.string()),
  /** true when composed client-side because the backend endpoint is absent */
  approximate:       z.boolean().optional(),
});
export type CoverageReport = z.infer<typeof CoverageReport>;

export interface CoverageTotals {
  expected: number; produced: number; missing: number; pages: number;
  publishers_expected: number; publishers_ok: number; publishers_partial: number;
  publishers_missing: number; publishers_dead: number; publishers_auth_blocked: number;
  publishers_scheduled_off: number; open_alerts: number;
  by_classification: Record<string, number>;
}

export function totalsOf(r: CoverageReport): CoverageTotals {
  const t = r.totals as Partial<CoverageTotals>;
  return {
    expected: t.expected ?? 0, produced: t.produced ?? 0, missing: t.missing ?? 0, pages: t.pages ?? 0,
    publishers_expected: t.publishers_expected ?? 0, publishers_ok: t.publishers_ok ?? 0,
    publishers_partial: t.publishers_partial ?? 0, publishers_missing: t.publishers_missing ?? 0,
    publishers_dead: t.publishers_dead ?? 0, publishers_auth_blocked: t.publishers_auth_blocked ?? 0,
    publishers_scheduled_off: t.publishers_scheduled_off ?? 0, open_alerts: t.open_alerts ?? 0,
    by_classification: (t.by_classification ?? {}) as Record<string, number>,
  };
}

export async function fetchCoverage(businessDate?: string): Promise<CoverageReport> {
  try {
    const res = await api.get("/api/dashboard/coverage", {
      params: businessDate ? { business_date: businessDate } : {},
      timeout: 60_000,
    });
    return CoverageReport.parse(res.data);
  } catch (err) {
    // Backend without the endpoint (older deploy) → compose client-side.
    if (axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 405)) {
      return composeCoverageClientSide(businessDate);
    }
    throw err;
  }
}

/* Client-side approximation. Publisher = every channel with ≥1 crawlable
   edition; expected = editions with is_crawlable && is_scraped; produced =
   last_issue_date === date. No NoPublishDays / CityCode knowledge here, so
   classification is only pending / new_gap / dead / auth_blocked. */
async function composeCoverageClientSide(businessDate?: string): Promise<CoverageReport> {
  const today = new Date();
  const dateISO = businessDate ?? isoDate(today);
  const d = new Date(dateISO + "T00:00:00");
  const ist = new Date(Date.now() + (5.5 * 60 + today.getTimezoneOffset()) * 60_000);
  const isToday = dateISO === isoDate(today);
  const windowOpen = isToday && ist.getHours() >= 4 && ist.getHours() < 14;

  const [pubs, auth] = await Promise.all([
    fetchPublishers({ includeUncrawled: false, businessDate: dateISO }),
    fetchAuthProfiles().catch(() => ({ rows: [] })),
  ]);
  const authBy = new Map(auth.rows.map((a) => [a.channel_id, a]));
  const publishers: PublisherCoverage[] = [];
  const editionLists = await Promise.all(pubs.map((p) => fetchEditions(p.channel_id).catch(() => [])));

  pubs.forEach((p, i) => {
    const eds = editionLists[i] ?? [];
    const expected = eds.filter((e) => e.is_crawlable && e.is_scraped);
    if (expected.length === 0) return;
    const a = authBy.get(p.channel_id);
    const authBlocked = !!a && (a.last_login_status === "failing" || a.last_login_status === "disabled");
    const missing: MissingEdition[] = [];
    let produced = 0, pages = 0, lastOk: string | null = null;
    for (const e of expected) {
      const last = e.last_issue_date;
      if (last && (!lastOk || last > lastOk)) lastOk = last;
      if (last === dateISO) { produced++; pages += e.last_issue_pages ?? 0; continue; }
      const days = last ? Math.round((d.getTime() - new Date(last + "T00:00:00").getTime()) / 86_400_000) : null;
      let cls: GapClass = "dead";
      if (authBlocked) cls = "auth_blocked";
      else if (windowOpen && days === 1) cls = "pending";
      else if (days !== null && days < 7) cls = "new_gap";
      missing.push({ edition_id: e.edition_id, city: e.city, edition_name: e.edition_name,
        classification: cls, last_ok_date: last, days_since_ok: days, review_reason: null,
        has_city_code: true, today_status: null });
    }
    const daysSince = lastOk ? Math.round((d.getTime() - new Date(lastOk + "T00:00:00").getTime()) / 86_400_000) : null;
    let status: PubStatus = "ok";
    if (produced === expected.length) status = "ok";
    else if (produced === 0) {
      status = authBlocked ? "auth_blocked" : (daysSince === null || daysSince >= 7) ? "dead"
             : (windowOpen && daysSince === 1) ? "pending" : "missing";
    } else status = "partial";
    const counts: Record<string, number> = {};
    for (const m of missing) counts[m.classification] = (counts[m.classification] ?? 0) + 1;
    publishers.push({
      channel_id: p.channel_id, channel_name: p.channel_name, category: p.category, language: p.language,
      expected: expected.length, produced, missing: missing.length, pages, status,
      last_ok_date: lastOk, days_since_ok: daysSince, no_publish_days: null, scheduled_off_today: false,
      needs_login: !!a, auth_required_for_content: !!a, auth_status: a?.last_login_status ?? null,
      auth_failure_count: a?.failure_count ?? null, auth_last_validated_at: a?.last_validated_at ?? null,
      auth_blocked: authBlocked, missing_editions: missing, classification_counts: counts,
    });
  });
  publishers.sort((x, y) => y.missing - x.missing || x.channel_name.localeCompare(y.channel_name));
  const sum = (k: keyof PublisherCoverage) => publishers.reduce((s, p) => s + (p[k] as number), 0);
  const byCls: Record<string, number> = {};
  for (const p of publishers) for (const [k, v] of Object.entries(p.classification_counts)) byCls[k] = (byCls[k] ?? 0) + v;
  const totals: CoverageTotals = {
    expected: sum("expected"), produced: sum("produced"), missing: sum("expected") - sum("produced"), pages: sum("pages"),
    publishers_expected: publishers.length,
    publishers_ok: publishers.filter((p) => p.status === "ok").length,
    publishers_partial: publishers.filter((p) => p.status === "partial").length,
    publishers_missing: publishers.filter((p) => ["missing", "dead", "auth_blocked", "pending"].includes(p.status)).length,
    publishers_dead: publishers.filter((p) => p.status === "dead").length,
    publishers_auth_blocked: publishers.filter((p) => p.status === "auth_blocked").length,
    publishers_scheduled_off: 0, open_alerts: 0, by_classification: byCls,
  };
  return {
    business_date: dateISO, weekday: d.toLocaleDateString("en-GB", { weekday: "long" }),
    is_today: isToday, crawl_window_open: windowOpen, generated_at: new Date().toISOString(),
    totals: { ...totals } as Record<string, unknown>, publishers, attempt_errors: [], open_alerts: [],
    notes: ["Approximated in the browser: the /api/dashboard/coverage endpoint is not deployed on this backend yet, " +
            "so publish-day schedules, CityCode activation, attempt errors and alerts are not included."]
      .concat(windowOpen ? ["Crawl window still open; counts can rise until 14:00 IST."] : []),
    approximate: true,
  };
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
